<template>
  <AdminShell>
    <div class="head">
      <h2>Users</h2>
      <router-link to="/admin/users/new" class="btn">Create User</router-link>
    </div>

    <table class="table">
      <thead>
        <tr>
          <th>Username</th>
          <th>Role</th>
          <th>Tenant</th>
          <th>Active</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in paginatedUsers" :key="u.id">
          <td>{{ u.username }}</td>
          <td>{{ u.role }}</td>
          <td>{{ u.tenant_name || "-" }}</td>
          <td>{{ u.is_active ? "Yes" : "No" }}</td>
        </tr>

        <tr v-if="!paginatedUsers.length">
          <td colspan="4">No users found</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <div class="page-info">
        Showing {{ startItem }} - {{ endItem }} of {{ users.length }}
      </div>

      <div class="page-controls">
        <select v-model.number="perPage" class="per-page">
          <option :value="5">5</option>
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>

        <button class="page-btn" :disabled="page === 1" @click="prevPage">
          Prev
        </button>

        <span class="page-number">Page {{ page }} / {{ totalPages }}</span>

        <button class="page-btn" :disabled="page === totalPages || totalPages === 0" @click="nextPage">
          Next
        </button>
      </div>
    </div>
  </AdminShell>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import AdminShell from "@/components/admin/AdminShell.vue";
import { getUsers } from "@/services/admin";

const users = ref([]);

const page = ref(1);
const perPage = ref(5);

onMounted(async () => {
  const res = await getUsers();
  users.value = res.data?.users || [];
});

const totalPages = computed(() => {
  return Math.ceil(users.value.length / perPage.value) || 0;
});

const paginatedUsers = computed(() => {
  const start = (page.value - 1) * perPage.value;
  const end = start + perPage.value;
  return users.value.slice(start, end);
});

const startItem = computed(() => {
  if (!users.value.length) return 0;
  return (page.value - 1) * perPage.value + 1;
});

const endItem = computed(() => {
  return Math.min(page.value * perPage.value, users.value.length);
});

function nextPage() {
  if (page.value < totalPages.value) {
    page.value += 1;
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
  }
}

watch(perPage, () => {
  page.value = 1;
});

watch(users, () => {
  if (page.value > totalPages.value) {
    page.value = totalPages.value || 1;
  }
});
</script>

<style scoped>
.head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.btn {
  background: #8b5cf6;
  color: #fff;
  text-decoration: none;
  padding: 10px 14px;
  border-radius: 10px;
}

.table {
  width: 100%;
  background: white;
  border-collapse: collapse;
}

th,
td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: center;
  vertical-align: middle;
}

.pagination {
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 12px 16px;
  border-radius: 10px;
}

.page-info {
  font-size: 14px;
  color: #555;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.per-page {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.page-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number {
  font-size: 14px;
  min-width: 90px;
  text-align: center;
}
</style>